import React, { CSSProperties, useCallback, useContext, useMemo, useState } from "react"

import { MinusIcon, PlusIcon, XMarkIcon } from "@heroicons/react/20/solid"
import { twMerge } from "tailwind-merge"

import { Bee } from "~/src/components/BeeKit"
import { type Color } from "~/src/types/color"
import { type Range } from "~/src/types/filter"

export type FilterValues = string[] | Range[] | Color[] | Range
export type FilterMap = { [section: string]: FilterValues }
export function filterMapCount(filterMap: FilterMap): number {
  return Object.keys(filterMap)
    .map((key) => {
      const filterValues = filterMap[key]
      return Array.isArray(filterValues) ? filterValues.length : 0
    })
    .reduce((acc, value) => acc + value, 0)
}

type ContextType = {
  isDisabled: boolean
  selectedItemMap: FilterMap
  onSectionChange: (section: string, newValues: FilterValues) => void
  onApply: () => void
}

const FilterContext = React.createContext<ContextType | null>(null)

export type FilterProps = {
  selectedItemMap: FilterMap
  className?: string
  open?: boolean
  disabled?: boolean
  onSectionChange?: (section: string, newValues: FilterValues) => void
  onApply?: () => void
  onClearAll?: () => void
  onClose?: () => void
}

export type FilterDesktopProps = {
  className?: string
}

export type FilterMobileProps = {
  onClearAll?: () => void
  onClose?: () => void
}

type FilterSectionHeaderProps = {
  title: string
}

type FilterSectionProps = {
  sectionKey: string
  title: string
}

type FilterStringSectionProps = FilterSectionProps & {
  items: string[]
}

type FilterRangeSectionProps = FilterSectionProps & {
  items: Range[]
}

type FilterSliderSectionProps = FilterSectionProps & {
  bounds: Range
}

type FilterColorSectionProps = FilterSectionProps & {
  items: Color[]
}

function FilterSectionHeader(props: React.PropsWithChildren<FilterSectionHeaderProps>) {
  const { title, children } = props
  const [isOpen, setIsOpen] = useState<boolean>(true)
  return (
    <div className="border-t py-2">
      <div
        className="flex list-none items-center justify-between text-gray-800 hover:cursor-pointer"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <h3 className="font-semibold">{title}</h3>
        {isOpen ? <MinusIcon className="h-4 w-4 text-gray-600" /> : <PlusIcon className="h-4 w-4 text-gray-600" />}
      </div>
      <div className={isOpen ? "visible" : "hidden pt-2"}>{children}</div>
    </div>
  )
}

function FilterStringSection(props: FilterStringSectionProps) {
  const { title, sectionKey, items } = props
  const filterContext = useContext(FilterContext)

  if (filterContext == null) {
    throw new Error("Filter sections must be used with a Filter component")
  }

  const selectedItems = useMemo(() => {
    return (filterContext.selectedItemMap[sectionKey] || []) as string[]
  }, [sectionKey, filterContext.selectedItemMap])

  const handleOnChange = useCallback(
    (item: string) => {
      const index = selectedItems.findIndex((selectedString: string) => selectedString == item)
      const newSelectedItems =
        index >= 0 ? [...selectedItems.slice(0, index), ...selectedItems.slice(index + 1)] : [...selectedItems, item]
      filterContext.onSectionChange(sectionKey, newSelectedItems)
    },
    [sectionKey, filterContext.selectedItemMap, filterContext.onSectionChange]
  )

  return items.length > 0 ? (
    <FilterSectionHeader title={title}>
      {items.map((item, index) => {
        return (
          <div key={item || index} className="py-1">
            <label className="flex items-center text-gray-800">
              <input
                type="checkbox"
                checked={selectedItems.includes(item)}
                onChange={() => handleOnChange(item)}
                className="mr-2 rounded border-gray-300 checked:bg-blue-900 hover:bg-blue-100"
              />
              {item}
            </label>
          </div>
        )
      })}
    </FilterSectionHeader>
  ) : null
}

function FilterRangeSection(props: FilterRangeSectionProps) {
  const { title, sectionKey, items } = props
  const filterContext = useContext(FilterContext)

  if (filterContext == null) {
    throw new Error("Filter sections must be used with a Filter component")
  }

  const selectedItems = useMemo(() => {
    return (filterContext.selectedItemMap[sectionKey] || []) as Range[]
  }, [sectionKey, filterContext.selectedItemMap])

  const handleOnChange = useCallback(
    (item: Range) => {
      const index = selectedItems.findIndex(
        (selectedRange: Range) => selectedRange.min === item.min && selectedRange.max === item.max
      )

      const newSelectedItems =
        index >= 0 ? [...selectedItems.slice(0, index), ...selectedItems.slice(index + 1)] : [...selectedItems, item]
      filterContext.onSectionChange(sectionKey, newSelectedItems)
    },
    [sectionKey, filterContext.selectedItemMap, filterContext.onSectionChange]
  )

  return items.length > 0 ? (
    <FilterSectionHeader title={title}>
      {items.map((item, index) => {
        const label = item.label ? item.label : `${item.min}-${item.max}`
        return (
          <div key={item.label || index} className="py-1">
            <label className="flex items-center text-gray-800">
              <input
                type="checkbox"
                checked={selectedItems.some(({ min, max }) => min === item.min && max === item.max)}
                onChange={() => handleOnChange(item)}
                className="mr-2 rounded border-gray-300 checked:bg-blue-900 hover:bg-blue-100"
              />
              {label}
            </label>
          </div>
        )
      })}
    </FilterSectionHeader>
  ) : null
}

function FilterSliderSection(props: FilterSliderSectionProps) {
  const { title, sectionKey, bounds } = props
  const filterContext = useContext(FilterContext)

  if (filterContext == null) {
    throw new Error("Filter sections must be used with a Filter component")
  }

  const selectedPriceRange = useMemo(() => {
    return (filterContext.selectedItemMap[sectionKey] || bounds) as Range
  }, [sectionKey, filterContext.selectedItemMap])

  const handleOnChange = useCallback(
    (range: Range) => {
      filterContext.onSectionChange(sectionKey, range)
    },
    [sectionKey, filterContext.onSectionChange]
  )

  return (
    <FilterSectionHeader title={title}>
      <div className="visible flex flex-col gap-2 pt-4">
        <div className="flex justify-between text-center text-xs text-white">
          <span className="min-w-xs rounded-sm bg-gray-600 p-2 px-1 py-px">${Math.floor(selectedPriceRange.min)}</span>
          <span className="min-w-xs rounded-sm bg-gray-600 p-2 px-1 py-px">${Math.ceil(selectedPriceRange.max)}</span>
        </div>
        <Bee.RangeInput
          min={bounds.min}
          max={bounds.max}
          left={{ value: Math.floor(selectedPriceRange.min) }}
          right={{ value: Math.ceil(selectedPriceRange.max) }}
          onChange={(item) => handleOnChange({ min: item[0], max: item[1] })}
          className="w-7/8"
        />
      </div>
    </FilterSectionHeader>
  )
}

function FilterColorSection(props: FilterColorSectionProps) {
  const { title, sectionKey, items } = props
  const filterContext = useContext(FilterContext)

  if (filterContext == null) {
    throw new Error("Filter sections must be used with a Filter component")
  }

  const selectedItems = useMemo(() => {
    return (filterContext.selectedItemMap[sectionKey] || []) as Color[]
  }, [sectionKey, filterContext.selectedItemMap])

  const handleOnChange = useCallback(
    (item: Color) => {
      const index = selectedItems.findIndex((selectedColor: Color) => selectedColor.colorName == item.colorName)
      const newSelectedItems =
        index >= 0 ? [...selectedItems.slice(0, index), ...selectedItems.slice(index + 1)] : [...selectedItems, item]
      filterContext.onSectionChange(sectionKey, newSelectedItems)
    },
    [sectionKey, filterContext.selectedItemMap, filterContext.onSectionChange]
  )

  return items.length > 0 ? (
    <FilterSectionHeader title={title}>
      {items.map((item, index) => {
        return (
          <div key={item.colorName || index} className="py-1">
            <label className="flex items-center text-gray-800">
              <input
                type="checkbox"
                checked={selectedItems.some(
                  ({ colorName, colorCode }) =>
                    colorName == item.colorName || (colorCode && colorCode == item.colorCode)
                )}
                onChange={() => handleOnChange(item)}
                className="mr-2 rounded border-gray-300 checked:bg-blue-900 hover:bg-blue-100"
              />
              {item.colorCode ? <ColorDot code={item.colorCode} /> : <MultiColorDot />}
              {item.colorName}
            </label>
          </div>
        )
      })}
    </FilterSectionHeader>
  ) : null
}

function ColorDot(props: { code: string }) {
  const { code } = props
  return <div className="mr-1 h-2 w-2 rounded-full border border-gray-300" style={{ backgroundColor: code }} />
}

function MultiColorDot() {
  const topLeftCode = "#dc2626"
  const topRightCode = "#f8b241"
  const bottomLeftCode = "#57c589"
  const bottomRightCode = "#2563eb"

  return (
    <div className="mr-1 grid grid-cols-2 grid-rows-2 gap-0">
      <div className="h-1 w-1 rounded-tl-full" style={{ backgroundColor: topLeftCode }} />
      <div className="h-1 w-1 rounded-tr-full" style={{ backgroundColor: topRightCode }} />
      <div className="h-1 w-1 rounded-bl-full" style={{ backgroundColor: bottomLeftCode }} />
      <div className="h-1 w-1 rounded-br-full" style={{ backgroundColor: bottomRightCode }} />
    </div>
  )
}

function FilterDesktop(props: React.PropsWithChildren<FilterDesktopProps>) {
  const { className, children } = props
  const filterContext = useContext(FilterContext)

  if (filterContext == null) {
    throw new Error("FilterDesktop must be used with a Filter component")
  }

  return (
    <div
      className={twMerge("hidden flex-col gap-4 pr-2 sm:flex")}
      style={{ "--filter-width": `${Filter.width}px` } as CSSProperties}
    >
      <div className={twMerge("bg-grey-50 h-full w-[var(--filter-width)]", className)}>
        <div className="px-1 pb-2">{children}</div>
        <Bee.Button
          onClick={filterContext.onApply}
          className={twMerge(
            "sticky bottom-3 left-0 z-20 w-[300px] px-2 pb-2",
            filterContext.isDisabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
          disabled={filterContext.isDisabled}
        >
          Apply
        </Bee.Button>
      </div>
    </div>
  )
}

function FilterMobile(props: React.PropsWithChildren<FilterMobileProps>) {
  const { onClearAll, onClose, children } = props
  const filterContext = useContext(FilterContext)

  if (filterContext == null) {
    throw new Error("FilterMobile must be used with a Filter component")
  }

  const numSelectedFilters = useMemo(
    () => filterMapCount(filterContext.selectedItemMap),
    [filterContext.selectedItemMap]
  )

  return (
    <div className={twMerge("fixed inset-0 z-50 flex flex-col overflow-y-auto bg-white sm:hidden")}>
      <div className="flex justify-between px-4 py-3.5">
        <div className="flex gap-3">
          <h1 className="text-lg font-bold">Filter {numSelectedFilters > 0 ? `(${numSelectedFilters})` : ""}</h1>
          <button className="text-xs text-gray-500 hover:cursor-pointer" onClick={onClearAll}>
            Clear all
          </button>
        </div>
        <XMarkIcon onClick={onClose} className="ml-1 h-6 w-6 text-gray-600 hover:cursor-pointer" />
      </div>
      <div className="px-6 pb-6">
        {children}
        <Bee.Button
          onClick={() => {
            filterContext.onApply()
            onClose?.()
          }}
          className="w-full cursor-pointer px-2 pb-2 disabled:cursor-not-allowed"
          disabled={filterContext.isDisabled}
        >
          Apply
        </Bee.Button>
      </div>
    </div>
  )
}

//region Component
export function Filter(props: React.PropsWithChildren<FilterProps>) {
  const {
    selectedItemMap,
    className,
    open = true,
    disabled = false,
    onSectionChange = () => {},
    onApply = () => {},
    onClearAll,
    onClose,
    children,
  } = props
  const providerValue = useMemo(
    () => ({
      isDisabled: disabled,
      selectedItemMap,
      onSectionChange,
      onApply,
    }),
    [disabled, onSectionChange]
  )

  return (
    open && (
      <FilterContext.Provider value={providerValue}>
        <FilterDesktop className={className}>{children}</FilterDesktop>
        <FilterMobile onClearAll={onClearAll} onClose={onClose}>
          {children}
        </FilterMobile>
      </FilterContext.Provider>
    )
  )
}

Filter.StringSection = FilterStringSection
Filter.RangeSection = FilterRangeSection
Filter.SliderSection = FilterSliderSection
Filter.ColorSection = FilterColorSection
//endregion

Filter.width = 300
